import { Static, Type } from '@sinclair/typebox';
import { Value } from '@sinclair/typebox/value';

import { CookieService } from './cookieService';

export const BUYER_REFRESH_TIMEOUT_MS = 15_000;
const LEASE_MS = 30_000;
const DATABASE = 'diamond-buyer-session';
const STORE = 'session';
const KEY = 'current';
const LOCK = 'diamond:buyer-session-refresh';
let localGeneration: string | undefined;

const RecordSchema = Type.Object({
  generation: Type.String(),
  attempt: Type.Optional(
    Type.Object({
      id: Type.String(),
      owner: Type.String(),
      expiresAt: Type.Number(),
      uncertain: Type.Boolean(),
    })
  ),
});

type SessionRecord = Static<typeof RecordSchema>;

function identifier() {
  const bytes = crypto.getRandomValues(new Uint32Array(4));
  return Array.from(bytes, (value) => value.toString(16)).join('-');
}

// Mutations, ownership checks and synchronous credential writes share one
// transaction. A localStorage read followed by a write is not an atomic claim.
async function updateSession<T>(
  update: (record: SessionRecord) => T
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let database: IDBDatabase | undefined;
    let transaction: IDBTransaction | undefined;
    let settled = false;
    const fail = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        transaction?.abort();
      } catch {
        /* Transaction already settled. */
      }
      database?.close();
      reject(new Error('Gagal memulihkan sesi. Silakan coba lagi.'));
    };
    const timer = setTimeout(fail, BUYER_REFRESH_TIMEOUT_MS);
    try {
      const request = indexedDB.open(DATABASE, 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore(STORE);
      };
      request.onerror = fail;
      request.onblocked = fail;
      request.onsuccess = () => {
        database = request.result;
        if (settled) {
          database.close();
          return;
        }
        database.onversionchange = () => database?.close();
        transaction = database.transaction(STORE, 'readwrite');
        transaction.onerror = fail;
        transaction.onabort = fail;
        const store = transaction.objectStore(STORE);
        const read = store.get(KEY);
        read.onsuccess = () => {
          try {
            const raw: unknown = read.result;
            let record: SessionRecord;
            if (raw === undefined) {
              record = { generation: identifier() };
            } else if (Value.Check(RecordSchema, raw)) {
              record = raw;
            } else {
              fail();
              return;
            }
            const result = update(record);
            store.put(record, KEY);
            if (transaction) {
              transaction.oncomplete = () => {
                settled = true;
                clearTimeout(timer);
                database?.close();
                resolve(result);
              };
            }
          } catch {
            fail();
          }
        };
      };
    } catch {
      fail();
    }
  });
}

export function getBuyerSessionGeneration() {
  return updateSession((record) => record.generation);
}

export function changeBuyerSession(
  effect: () => void,
  expected?: string,
  authorization?: string
) {
  return updateSession((record) => {
    if (expected !== undefined && record.generation !== expected) return false;
    const currentToken = CookieService.getJwtToken();
    if (
      authorization !== undefined &&
      currentToken !== undefined &&
      authorization !== `Bearer ${currentToken}`
    )
      return false;
    record.generation = identifier();
    delete record.attempt;
    effect();
    localGeneration = record.generation;
    return record.generation;
  });
}

export type CoordinatedRefreshResult =
  | { status: 'success'; data: { token: string; refresh_token: string } }
  | { status: 'invalid-session' }
  | { status: 'discarded' }
  | { status: 'temporary-failure'; uncertain?: boolean };

type Claim = 'owned' | 'waiting' | 'uncertain' | 'changed' | 'discarded';

export async function coordinateBuyerRefresh(
  generation: string,
  refreshToken: string,
  refresh: () => Promise<CoordinatedRefreshResult>,
  commit: (data: { token: string; refresh_token: string }) => void
): Promise<CoordinatedRefreshResult> {
  const attemptId = identifier();
  const owner = identifier();
  const deadline = Date.now() + LEASE_MS;
  const run = async (): Promise<CoordinatedRefreshResult> => {
    while (Date.now() < deadline) {
      const claim = await updateSession<Claim>((record) => {
        if (record.generation !== generation) return 'discarded';
        if (CookieService.getRefreshToken() !== refreshToken) return 'changed';
        if (record.attempt) {
          if (
            record.attempt.uncertain ||
            record.attempt.expiresAt <= Date.now()
          ) {
            record.attempt.uncertain = true;
            return 'uncertain';
          }
          return 'waiting';
        }
        record.attempt = {
          id: attemptId,
          owner,
          expiresAt: Date.now() + LEASE_MS,
          uncertain: false,
        };
        return 'owned';
      });
      if (claim === 'discarded') return { status: 'discarded' };
      if (claim === 'uncertain') return { status: 'temporary-failure' };
      if (claim === 'changed') {
        return updateSession((record): CoordinatedRefreshResult => {
          if (record.generation !== generation) return { status: 'discarded' };
          const token = CookieService.getJwtToken();
          const refresh_token = CookieService.getRefreshToken();
          if (!token || !refresh_token) return { status: 'discarded' };
          const data = { token, refresh_token };
          commit(data);
          return { status: 'success', data };
        });
      }
      if (claim === 'waiting') {
        await new Promise((resolve) => setTimeout(resolve, 100));
        continue;
      }
      const result = await refresh();
      return updateSession((record): CoordinatedRefreshResult => {
        if (
          record.generation !== generation ||
          record.attempt?.id !== attemptId ||
          CookieService.getRefreshToken() !== refreshToken
        )
          return { status: 'discarded' };
        if (
          record.attempt.expiresAt <= Date.now() ||
          (result.status === 'temporary-failure' && result.uncertain)
        ) {
          record.attempt.uncertain = true;
          return { status: 'temporary-failure' };
        }
        if (result.status === 'success') commit(result.data);
        delete record.attempt;
        return result;
      });
    }
    return { status: 'temporary-failure' };
  };
  try {
    if (!navigator.locks) return await run();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), LEASE_MS);
    try {
      return await navigator.locks.request(
        LOCK,
        { signal: controller.signal },
        run
      );
    } finally {
      clearTimeout(timer);
    }
  } catch {
    return { status: 'temporary-failure' };
  }
}

export function watchBuyerSession(onChanged: () => void) {
  let stopped = false;
  const check = async () => {
    try {
      const generation = await getBuyerSessionGeneration();
      if (stopped) return;
      if (localGeneration === undefined) localGeneration = generation;
      if (generation !== localGeneration) {
        stopped = true;
        onChanged();
      }
    } catch {
      // Request recovery reports unavailable coordination; never infer expiry here.
    }
  };
  const onStorage = (event: StorageEvent) => {
    if (event.key === 'session-store' || event.key === null) void check();
  };
  window.addEventListener('storage', onStorage);
  void check();
  return () => {
    stopped = true;
    window.removeEventListener('storage', onStorage);
  };
}
